const translitMap = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "e",
  ж: "zh",
  з: "z",
  и: "i",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "sch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
};

const SAFE_HTML_TAGS = new Set([
  "a",
  "blockquote",
  "br",
  "code",
  "em",
  "figcaption",
  "figure",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "hr",
  "iframe",
  "img",
  "li",
  "ol",
  "p",
  "pre",
  "strong",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
]);

function trimPunctuation(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/\s([.,!?;:])/g, "$1")
    .trim();
}

export function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .split("")
    .map((char) => translitMap[char] ?? char)
    .join("")
    .replace(/[^a-z0-9\u0400-\u04ff]+/gi, "-")
    .replace(/^-+|-+$/g, "");
}

export function categoryToSlug(category) {
  return slugify(category);
}

export function formatBlogDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
}

export function estimateReadingTime(blocks) {
  const text = (blocks || [])
    .map((block) => {
      if (Array.isArray(block.items)) {
        return block.items.join(" ");
      }
      return block.text || block.code || "";
    })
    .join(" ");

  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(3, Math.round(words / 180));
}

export function excerptFromBlocks(blocks) {
  const paragraph = (blocks || []).find((block) => block.type === "paragraph");
  return trimPunctuation(paragraph?.text || "");
}

export function buildHeadingId(text) {
  return slugify(text);
}

function extractHeadingsFromHtml(content) {
  if (typeof window === "undefined" || !content) {
    return [];
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const headings = Array.from(doc.querySelectorAll("h2, h3"));

  return headings.map((heading) => ({
    id: buildHeadingId(heading.textContent || ""),
    text: trimPunctuation(heading.textContent || ""),
    level: heading.tagName === "H3" ? 3 : 2,
  }));
}

export function getArticleToc(articleItem) {
  if (articleItem?.contentFormat === "html") {
    return extractHeadingsFromHtml(articleItem.content || "");
  }

  return (articleItem?.blocks || [])
    .filter((block) => block.type === "heading" && [2, 3].includes(block.level))
    .map((block) => ({
      id: buildHeadingId(block.text),
      text: block.text,
      level: block.level,
    }));
}

export function stripMarkdown(value) {
  return trimPunctuation(
    String(value || "")
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/^>\s?/gm, "")
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/^[-*+]\s+/gm, "")
      .replace(/^\d+\.\s+/gm, "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/__([^_]+)__/g, "$1")
      .replace(/[*_~]/g, " ")
  );
}

export function excerptFromMarkdown(value, maxLength = 160) {
  const plain = stripMarkdown(value);
  if (!plain) return "";
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 3).trimEnd()}...`;
}

export function stripHtml(value) {
  if (!value) return "";
  if (typeof window === "undefined") {
    return String(value).replace(/<[^>]+>/g, " ");
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(String(value), "text/html");
  return trimPunctuation(doc.body.textContent || "");
}

export function excerptFromHtml(value, maxLength = 160) {
  const plain = stripHtml(value);
  if (!plain) return "";
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 3).trimEnd()}...`;
}

export function sanitizeRichHtml(value) {
  if (!value) return "";
  if (typeof window === "undefined") {
    return String(value);
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(String(value), "text/html");

  const sanitizeNode = (node) => {
    if (node.nodeType === window.Node.TEXT_NODE) {
      return;
    }

    if (node.nodeType !== window.Node.ELEMENT_NODE) {
      node.remove();
      return;
    }

    const tagName = node.tagName.toLowerCase();
    if (!SAFE_HTML_TAGS.has(tagName)) {
      const parent = node.parentNode;
      while (node.firstChild) {
        parent?.insertBefore(node.firstChild, node);
      }
      node.remove();
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value || "";
      const isAllowed =
        name === "alt" ||
        name === "src" ||
        name === "title" ||
        name === "width" ||
        name === "height" ||
        name === "target" ||
        name === "rel" ||
        name === "allow" ||
        name === "allowfullscreen" ||
        name === "frameborder" ||
        name === "loading" ||
        name === "class";

      if (!isAllowed && !(tagName === "a" && name === "href")) {
        node.removeAttribute(attribute.name);
        return;
      }

      if (["src", "href"].includes(name)) {
        const normalized = value.trim().toLowerCase();
        if (
          normalized.startsWith("javascript:") ||
          normalized.startsWith("data:text/html")
        ) {
          node.removeAttribute(attribute.name);
        }
      }
    });

    if (tagName === "a") {
      node.setAttribute("rel", "noopener noreferrer nofollow");
      if (!node.getAttribute("target")) {
        node.setAttribute("target", "_blank");
      }
    }

    if (tagName === "iframe") {
      const src = (node.getAttribute("src") || "").trim();
      if (!/^https?:\/\//i.test(src)) {
        node.remove();
        return;
      }
      node.setAttribute("loading", "lazy");
      node.setAttribute(
        "allow",
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      );
      node.setAttribute("allowfullscreen", "true");
    }

    if (tagName === "img") {
      node.setAttribute("loading", "lazy");
    }

    Array.from(node.childNodes).forEach(sanitizeNode);
  };

  Array.from(doc.body.childNodes).forEach(sanitizeNode);

  Array.from(doc.body.querySelectorAll("h2, h3")).forEach((heading) => {
    heading.id = buildHeadingId(heading.textContent || "");
  });

  return doc.body.innerHTML;
}

function flushParagraph(buffer, blocks) {
  if (!buffer.length) return;
  blocks.push({
    type: "paragraph",
    text: trimPunctuation(buffer.join(" ")),
  });
  buffer.length = 0;
}

function flushList(items, ordered, blocks) {
  if (!items.length) return;
  blocks.push({
    type: "list",
    ordered,
    items: [...items],
  });
  items.length = 0;
}

export function markdownToBlocks(value) {
  const markdown = String(value || "").replace(/\r\n/g, "\n");
  if (!markdown.trim()) return [];

  const lines = markdown.split("\n");
  const blocks = [];
  const paragraphBuffer = [];
  const listBuffer = [];
  let inCodeBlock = false;
  let codeLanguage = "";
  let codeBuffer = [];
  let listOrdered = false;

  const flushAll = () => {
    flushParagraph(paragraphBuffer, blocks);
    flushList(listBuffer, listOrdered, blocks);
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      flushParagraph(paragraphBuffer, blocks);
      flushList(listBuffer, listOrdered, blocks);

      if (inCodeBlock) {
        blocks.push({
          type: "code",
          language: codeLanguage || "text",
          code: codeBuffer.join("\n"),
        });
        inCodeBlock = false;
        codeLanguage = "";
        codeBuffer = [];
        return;
      }

      inCodeBlock = true;
      codeLanguage = trimmed.slice(3).trim();
      codeBuffer = [];
      return;
    }

    if (inCodeBlock) {
      codeBuffer.push(rawLine);
      return;
    }

    if (!trimmed) {
      flushAll();
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushAll();
      const level = Math.min(headingMatch[1].length, 3);
      blocks.push({
        type: "heading",
        level: level === 1 ? 2 : level,
        text: trimPunctuation(headingMatch[2]),
      });
      return;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      flushParagraph(paragraphBuffer, blocks);
      if (listBuffer.length && !listOrdered) {
        flushList(listBuffer, listOrdered, blocks);
      }
      listOrdered = true;
      listBuffer.push(trimPunctuation(orderedMatch[1]));
      return;
    }

    const unorderedMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    if (unorderedMatch) {
      flushParagraph(paragraphBuffer, blocks);
      if (listBuffer.length && listOrdered) {
        flushList(listBuffer, listOrdered, blocks);
      }
      listOrdered = false;
      listBuffer.push(trimPunctuation(unorderedMatch[1]));
      return;
    }

    if (listBuffer.length) {
      flushList(listBuffer, listOrdered, blocks);
    }

    paragraphBuffer.push(trimPunctuation(trimmed));
  });

  if (inCodeBlock && codeBuffer.length) {
    blocks.push({
      type: "code",
      language: codeLanguage || "text",
      code: codeBuffer.join("\n"),
    });
  }

  flushParagraph(paragraphBuffer, blocks);
  flushList(listBuffer, listOrdered, blocks);

  return blocks;
}

export function formatArticleDocumentTitle(articleTitle) {
  const fullTitle = trimPunctuation(
    `${articleTitle || "Блог Энилекс.Контроль"} | Блог Энилекс.Контроль`
  );
  if (fullTitle.length <= 60) {
    return fullTitle;
  }
  return `${fullTitle.slice(0, 57).trimEnd()}...`;
}
